import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authAppHref, loginHref, shouldUseExternalAuthLinks } from '@/utils/authLinks';

export default function SignupPage() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (shouldUseExternalAuthLinks) {
      window.location.replace(authAppHref);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !fullName || !agreeTerms) return;
    if (password !== confirmPassword) return;
    setLoading(true);
    // Simulate registration flow
    setTimeout(() => setLoading(false), 2000);
  };

  if (shouldUseExternalAuthLinks) {
    return null;
  }

  return (
    <div className="min-h-screen bg-cream-50 flex">
      {/* Left — Image */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=A%20quiet%20garden%20courtyard%20with%20soft%20morning%20light%20warm%20stone%20pathway%20surrounded%20by%20lush%20green%20plants%20and%20small%20white%20flowers%20peaceful%20meditative%20atmosphere%20warm%20beige%20and%20sage%20green%20tones%20no%20people%20editorial%20photography%20style%20tranquil%20and%20reflective%20mood&width=900&height=1200&seq=auth2&orientation=portrait"
          alt="Peaceful garden courtyard"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal-400/30 via-transparent to-charcoal-400/10" />
        <div className="absolute bottom-12 left-10 right-10 z-10">
          <h2 className="font-serif text-3xl text-white font-medium leading-snug mb-3">
            Begin your journey
            <br />
            <span className="italic font-light">of stewardship.</span>
          </h2>
          <p className="text-white/70 text-sm leading-relaxed max-w-xs">
            Join a movement of people choosing peace, intentionality, and purposeful financial practice.
          </p>
        </div>
      </div>

      {/* Right — Form */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 md:px-12 lg:px-20">
        <div className="w-full max-w-sm">
          {/* Mobile header */}
          <div className="lg:hidden mb-10 text-center">
            <Link
              to="/"
              className="text-xs font-semibold tracking-[0.2em] uppercase text-charcoal-400 hover:text-sage-500 transition-colors"
            >
              LET MONEY SERVE
            </Link>
          </div>

          <div
            className={`transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <h1 className="font-serif text-2xl md:text-3xl text-charcoal-400 font-medium mb-2">
              Create Account
            </h1>
            <p className="text-sm text-charcoal-100 mb-8 leading-relaxed">
              Start practicing financial stewardship alongside a supportive community.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div
              className={`transition-all duration-700 delay-100 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <label htmlFor="fullName" className="block text-xs font-medium text-charcoal-300 mb-1.5">
                Full Name
              </label>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Maria Santos"
                required
                className="w-full px-4 py-3 bg-white border border-sand-100 rounded-lg text-sm text-charcoal-400 placeholder:text-charcoal-50 focus:outline-none focus:border-sage-300 focus:ring-1 focus:ring-sage-300 transition-all"
              />
            </div>

            <div
              className={`transition-all duration-700 delay-150 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <label htmlFor="email" className="block text-xs font-medium text-charcoal-300 mb-1.5">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full px-4 py-3 bg-white border border-sand-100 rounded-lg text-sm text-charcoal-400 placeholder:text-charcoal-50 focus:outline-none focus:border-sage-300 focus:ring-1 focus:ring-sage-300 transition-all"
              />
            </div>

            <div
              className={`transition-all duration-700 delay-200 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <label htmlFor="password" className="block text-xs font-medium text-charcoal-300 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  required
                  className="w-full px-4 py-3 bg-white border border-sand-100 rounded-lg text-sm text-charcoal-400 placeholder:text-charcoal-50 focus:outline-none focus:border-sage-300 focus:ring-1 focus:ring-sage-300 transition-all pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-charcoal-100 hover:text-charcoal-300 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
            </div>

            <div
              className={`transition-all duration-700 delay-250 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <label htmlFor="confirmPassword" className="block text-xs font-medium text-charcoal-300 mb-1.5">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                className="w-full px-4 py-3 bg-white border border-sand-100 rounded-lg text-sm text-charcoal-400 placeholder:text-charcoal-50 focus:outline-none focus:border-sage-300 focus:ring-1 focus:ring-sage-300 transition-all"
              />
            </div>

            <div
              className={`transition-all duration-700 delay-300 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-sand-100 text-sage-500 focus:ring-sage-300 accent-sage-500 shrink-0"
                />
                <span className="text-xs text-charcoal-200 leading-relaxed">
                  I agree to the{' '}
                  <a href="#" onClick={(e) => e.preventDefault()} className="text-sage-500 hover:text-sage-600 transition-colors">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="#" onClick={(e) => e.preventDefault()} className="text-sage-500 hover:text-sage-600 transition-colors">
                    Privacy Policy
                  </a>
                </span>
              </label>
            </div>

            <div
              className={`transition-all duration-700 delay-400 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-charcoal-400 text-white py-3 rounded-full text-sm font-medium hover:bg-charcoal-300 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </button>
            </div>
          </form>

          {/* Divider */}
          <div className="mt-8 mb-8 flex items-center gap-4">
            <span className="flex-1 h-px bg-sand-100" />
            <span className="text-xs text-charcoal-50 font-medium">or</span>
            <span className="flex-1 h-px bg-sand-100" />
          </div>

          {/* Social */}
          <div
            className={`transition-all duration-700 delay-500 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <button
              type="button"
              className="w-full flex items-center justify-center gap-3 bg-white border border-sand-100 text-charcoal-300 py-3 rounded-full text-sm font-medium hover:bg-sand-50 transition-all duration-300"
            >
              <i className="ri-google-fill text-base" />
              Continue with Google
            </button>
          </div>

          {/* Footer link */}
          <p
            className={`mt-8 text-center text-sm text-charcoal-200 transition-all duration-700 delay-500 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            Already have an account?{' '}
            <a
              href={loginHref}
              className="text-sage-500 font-medium hover:text-sage-600 transition-colors"
            >
              Sign in
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
